/**
 * RFM Segment Card Component
 *
 * Displays an individual RFM segment overview with:
 * - Segment name and icon
 * - Customer count and percentage
 * - Total and average revenue
 * - Color-coded background based on segment priority
 * - Click handler to navigate to segment details
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/frontend/components/ui/card";
import { Badge } from "@/frontend/components/ui/badge";
import {
  Trophy, Heart, TrendingUp, Sparkles, Star,
  AlertCircle, Moon, AlertTriangle, Users, Bed, XCircle
} from "lucide-react";
import type { RFMSegmentData, RFMSegment } from "@/shared/types/rfm-analytics";
import { SEGMENT_DEFINITIONS } from "@/shared/lib/rfm-constants";
import { SEGMENT_CARD_TRANSLATIONS } from "@/shared/lib/rfm-translations";

interface RFMSegmentCardProps {
  segment: RFMSegmentData;
  onClick?: () => void;
}

// Icon mapping for each segment
const SEGMENT_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  "Champions": Trophy,
  "Loyal Customers": Heart,
  "Potential Loyalists": TrendingUp,
  "New Customers": Sparkles,
  "Promising": Star,
  "Need Attention": AlertCircle,
  "About to Sleep": Moon,
  "At Risk": AlertTriangle,
  "Can't Lose Them": Users,
  "Hibernating": Bed,
  "Lost": XCircle
};

export function RFMSegmentCard({ segment, onClick }: RFMSegmentCardProps) {
  const metadata = SEGMENT_DEFINITIONS[segment.segment as RFMSegment];
  const Icon = SEGMENT_ICONS[segment.segment as string] || Users;

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Get color classes based on segment priority
  const getColorClasses = () => {
    if (metadata.color.includes('green')) {
      return {
        card: 'border-green-200 hover:border-green-300 bg-green-50/50',
        badge: 'bg-green-100 text-green-800 border-green-200',
        icon: 'text-green-600'
      };
    } else if (metadata.color.includes('blue')) {
      return {
        card: 'border-blue-200 hover:border-blue-300 bg-blue-50/50',
        badge: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: 'text-blue-600'
      };
    } else if (metadata.color.includes('yellow')) {
      return {
        card: 'border-yellow-200 hover:border-yellow-300 bg-yellow-50/50',
        badge: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: 'text-yellow-600'
      };
    } else if (metadata.color.includes('red')) {
      return {
        card: 'border-red-200 hover:border-red-300 bg-red-50/50',
        badge: 'bg-red-100 text-red-800 border-red-200',
        icon: 'text-red-600'
      };
    } else {
      return {
        card: 'border-gray-200 hover:border-gray-300 bg-gray-50/50',
        badge: 'bg-gray-100 text-gray-800 border-gray-200',
        icon: 'text-gray-600'
      };
    }
  };

  const colors = getColorClasses();

  return (
    <Card
      className={`transition-all duration-200 cursor-pointer ${colors.card} hover:shadow-md`}
      onClick={onClick}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg bg-white shadow-sm`}>
              <Icon className={`h-5 w-5 ${colors.icon}`} />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold">
                {segment.segment}
              </CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                {metadata.description}
              </p>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Customer Statistics */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">{SEGMENT_CARD_TRANSLATIONS.customers}</p>
            <p className="text-2xl font-bold">{segment.customerCount.toLocaleString()}</p>
          </div>
          <Badge variant="outline" className={colors.badge}>
            {segment.percentage.toFixed(1)}%
          </Badge>
        </div>

        {/* Revenue Statistics */}
        <div className="grid grid-cols-2 gap-4 pt-4 border-t">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">{SEGMENT_CARD_TRANSLATIONS.totalRevenue}</p>
            <p className="text-sm font-semibold mt-1">
              {formatCurrency(segment.totalRevenue)}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">{SEGMENT_CARD_TRANSLATIONS.avgRevenue}</p>
            <p className="text-sm font-semibold mt-1">
              {formatCurrency(segment.averageRevenue)}
            </p>
          </div>
        </div>

        {/* Actionable Insight */}
        {metadata.recommendations && metadata.recommendations.length > 0 && (
          <div className="pt-4 border-t">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">{SEGMENT_CARD_TRANSLATIONS.keyAction}</p>
            <p className="text-sm text-gray-700">
              {metadata.recommendations[0]}
            </p>
          </div>
        )}

        {/* More Customers Indicator */}
        {segment.hasMore && (
          <div className="pt-2 text-center">
            <p className="text-xs text-gray-500">
              {SEGMENT_CARD_TRANSLATIONS.clickToView}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
